import { useEffect, useRef, useState } from "react";
import { api, is429, retryAfterSec } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Lock, ArrowRight } from "lucide-react";

export default function Enrollment() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [consents, setConsents] = useState({ a: false, b: false, c: false, d: false });
  const [busy, setBusy] = useState(false);
  const payBtnRef = useRef(null);
  const cooldownTimerRef = useRef(null);

  useEffect(() => () => { if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current); }, []);

  // Approved enrollment page must not be indexed by search engines. Inject noindex meta on mount.
  useEffect(() => {
    const prior = document.querySelector('meta[name="robots"]');
    const meta = document.createElement("meta");
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", "noindex, nofollow, noarchive, nosnippet");
    document.head.appendChild(meta);
    const priorTitle = document.title;
    document.title = "Private Enrollment — Bounce City University";
    return () => {
      try { document.head.removeChild(meta); } catch { /* ignore */ }
      if (prior) document.head.appendChild(prior);
      document.title = priorTitle;
    };
  }, []);

  const ready = email && code && Object.values(consents).every(Boolean);

  const submit = async () => {
    if (!ready) { toast.error("Complete the enrollment checklist and enter your approval code."); return; }
    // Limited-time $5,000 mentorship offer: send approved buyer to the Square
    // hosted checkout in the same tab. Stripe backend flow is preserved for
    // future/legacy use but not invoked during the current offer window.
    const url = process.env.REACT_APP_MENTORSHIP_CHECKOUT_URL || "https://square.link/u/DV0t6n3H";
    setBusy(true);
    window.location.href = url;
  };

  const STEPS = ["Application approved", "Enrollment call completed", "Mentorship agreement reviewed and signed", "Payment completed", "Written enrollment confirmation received"];

  return (
    <div data-testid="enrollment-page" className="sect-navy pt-32 pb-24 min-h-screen">
      <div className="bcu-container max-w-5xl">
        <p className="bcu-eyebrow">Approved Applicants Only</p>
        <h1 className="mt-5 text-4xl md:text-6xl lg:text-7xl font-display leading-[1.02]">Complete your <span className="text-[#F7931E]">Bounce City University</span> enrollment.</h1>
        <p className="mt-6 text-[#C7CFDD] max-w-2xl">This page is intended for approved applicants who have completed the enrollment call and signed the mentorship agreement.</p>

        <div className="mt-12">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-bold">Enrollment Checklist</div>
          <ol className="mt-4 grid md:grid-cols-5 gap-3">
            {STEPS.map((s, i) => {
              const c = i % 3 === 0 ? "text-[#F7931E]" : i % 3 === 1 ? "text-[#168CFF]" : "text-[#78C800]";
              return (
                <li key={i} className="bcu-card p-4">
                  <div className={`font-display text-2xl ${c}`}>{String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-2 text-sm text-white">{s}</div>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <div className="bcu-card bcu-card-glow-blue p-8">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#168CFF] font-bold">Applicant Information</div>
            <label className="block mt-5"><span className="bcu-label">Approved applicant email <span className="text-[#F7931E]">*</span></span>
              <input data-testid="enrol-email" className="bcu-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </label>
            <label className="block mt-4"><span className="bcu-label">Approval code <span className="text-[#F7931E]">*</span></span>
              <input data-testid="enrol-code" className="bcu-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Provided by enrollment team" />
            </label>
            <p className="mt-3 text-[11px] text-[#7A8296]">Your approval code is issued by the enrollment team after the mentorship agreement is signed.</p>
          </div>

          <div className="bcu-card bcu-card-glow-orange p-8">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-bold">Program Investment</div>
            <div className="mt-3 flex flex-col gap-1">
              <span className="inline-flex items-center px-2.5 py-0.5 border border-[#F7931E]/40 bg-[#F7931E]/10 text-[#F7931E] text-[10px] tracking-[0.22em] uppercase font-semibold rounded self-start">Limited-Time Offer</span>
              <div className="mt-2 text-[11px] tracking-[0.22em] uppercase text-[#7A7A7A] font-semibold">Regular Investment</div>
              <div className="font-display text-2xl text-[#7A7A7A] line-through opacity-70">$10,000</div>
              <div className="mt-2 text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-semibold">Limited-Time Enrollment</div>
              <div className="font-display text-5xl text-white font-semibold" data-testid="enrollment-offer-price">$5,000</div>
            </div>
            <div className="mt-1 text-sm text-[#C7CFDD]">USD · one-time payment</div>
            <ul className="mt-6 space-y-2 text-sm text-white">
              <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#78C800]" /> Secure Stripe hosted checkout</li>
              <li className="flex items-center gap-2"><Lock className="h-4 w-4 text-[#168CFF]" /> No card data stored on this site</li>
            </ul>
          </div>
        </div>

        <div className="mt-8 space-y-3">
          {[
            { k: "a", t: "I confirm that I have been approved for enrollment." },
            { k: "b", t: "I confirm that I have reviewed and signed the program agreement." },
            { k: "c", t: "I understand the refund and cancellation terms." },
            { k: "d", t: "I understand that results and funding are not guaranteed." },
          ].map((c) => (
            <label key={c.k} className="flex items-start gap-3 border border-[rgba(255,255,255,0.10)] p-4 rounded-lg cursor-pointer bg-[#0D0D0D]">
              <input type="checkbox" data-testid={`enrol-consent-${c.k}`} checked={consents[c.k]} onChange={(e) => setConsents((s) => ({ ...s, [c.k]: e.target.checked }))} className="mt-1 h-4 w-4 accent-[#F7931E]" />
              <span className="text-sm text-[#C7CFDD]">{c.t}</span>
            </label>
          ))}
        </div>

        <div className="mt-8">
          <button ref={payBtnRef} type="button" onClick={submit} disabled={!ready || busy} data-testid="enrol-pay-btn" className="bcu-btn-primary">
            {busy ? "Starting secure checkout…" : (<>Complete secure payment <ArrowRight className="h-4 w-4" /></>)}
          </button>
        </div>
      </div>
    </div>
  );
}
